// Los Mundos de Zarek — Galaxia del Conocimiento v2
// Experiencia AAA estilo Mario Galaxy: planetas 3D con arte real,
// aterrizaje espacial, estrellas fugaces, nebulosas, Zarek flotante.
// MURO DE PRIVACIDAD: NUNCA puntajes, ICE, Z-scores ni radar.
import { useEffect, useState, useMemo, useCallback } from "react";
import { api } from "@/lib/api";
import { OWL_CLASSIC, OWL_CELEBRATION } from "@/lib/avatars";
import { motion, AnimatePresence } from "framer-motion";
import {
  Lock, Loader2, Rocket, Sparkles, ChevronLeft, Play, Star, Trophy,
} from "lucide-react";

// ── Planet Art & Config ──────────────────────────────────────────────────
const PLANETS = [
  { name: "Nebulosa Aritmética", img: "/planets/forest.jpg", glow: "#3AE880", glowRgb: "58,232,128", ring: "#44FFaa" },
  { name: "Océano Algebraico", img: "/planets/ocean.jpg", glow: "#44BBFF", glowRgb: "68,187,255", ring: "#66DDFF" },
  { name: "Cristal Geométrico", img: "/planets/crystal.jpg", glow: "#C466FF", glowRgb: "196,102,255", ring: "#D488FF" },
  { name: "Volcán Lógico", img: "/planets/volcano.jpg", glow: "#FF7744", glowRgb: "255,119,68", ring: "#FF9966" },
  { name: "Estrella Maestra", img: "/planets/star.jpg", glow: "#FFD700", glowRgb: "255,215,0", ring: "#FFED88" },
];

const PLANET_POS = [
  { x: 18, y: 72 },
  { x: 70, y: 58 },
  { x: 25, y: 38 },
  { x: 72, y: 22 },
  { x: 48, y: 5 },
];

// ── Stars ────────────────────────────────────────────────────────────────
function StarField() {
  const stars = useMemo(() =>
    Array.from({ length: 200 }, (_, i) => ({
      id: i,
      x: Math.random() * 100, y: Math.random() * 100,
      s: Math.random() * 2.4 + 0.4,
      d: Math.random() * 5, dur: Math.random() * 3 + 2,
      o: Math.random() * 0.7 + 0.15,
    })),
  []);
  return (
    <div className="absolute inset-0 overflow-hidden pointer-events-none" aria-hidden>
      {stars.map((s) => (
        <div
          key={s.id}
          className="absolute rounded-full bg-white"
          style={{
            left: `${s.x}%`, top: `${s.y}%`,
            width: s.s, height: s.s, opacity: s.o,
            animation: `twinkle ${s.dur}s ${s.d}s ease-in-out infinite alternate`,
          }}
        />
      ))}
    </div>
  );
}

// ── Shooting Stars ───────────────────────────────────────────────────────
function ShootingStars() {
  const meteors = useMemo(() =>
    Array.from({ length: 4 }, (_, i) => ({
      id: i,
      delay: i * 5 + Math.random() * 3,
      duration: 1.5 + Math.random(),
      startX: 20 + Math.random() * 60,
      startY: Math.random() * 30,
    })),
  []);
  return (
    <div className="absolute inset-0 overflow-hidden pointer-events-none" aria-hidden>
      {meteors.map((m) => (
        <div
          key={m.id}
          className="absolute w-[2px] h-[80px]"
          style={{
            left: `${m.startX}%`, top: `${m.startY}%`,
            background: "linear-gradient(to bottom, transparent, rgba(255,255,255,0.8), transparent)",
            transform: "rotate(-35deg)",
            animation: `shootingStar ${m.duration}s ${m.delay}s linear infinite`,
            opacity: 0,
          }}
        />
      ))}
    </div>
  );
}

// ── Nebulae ──────────────────────────────────────────────────────────────
function Nebulae() {
  return (
    <div className="absolute inset-0 pointer-events-none" aria-hidden>
      <div className="absolute top-[5%] left-[5%] w-[50vw] h-[40vw] rounded-full opacity-[0.06] blur-[120px]"
        style={{ background: "radial-gradient(circle, #B44AFF, transparent)" }} />
      <div className="absolute bottom-[15%] right-[0%] w-[45vw] h-[35vw] rounded-full opacity-[0.05] blur-[100px]"
        style={{ background: "radial-gradient(circle, #00AAFF, transparent)" }} />
      <div className="absolute top-[45%] left-[40%] w-[30vw] h-[30vw] rounded-full opacity-[0.04] blur-[90px]"
        style={{ background: "radial-gradient(circle, #FF6B35, transparent)" }} />
      <div className="absolute top-[70%] left-[15%] w-[25vw] h-[20vw] rounded-full opacity-[0.035] blur-[80px]"
        style={{ background: "radial-gradient(circle, #3AE880, transparent)" }} />
    </div>
  );
}

// ── Energy Path (SVG) ────────────────────────────────────────────────────
function EnergyPath({ etapas }) {
  const pts = etapas.map((_, i) => PLANET_POS[i % PLANET_POS.length]);
  let d = `M ${pts[0].x} ${pts[0].y}`;
  for (let i = 1; i < pts.length; i++) {
    const p = pts[i - 1], c = pts[i];
    d += ` C ${p.x + (c.x - p.x) * 0.5} ${p.y}, ${p.x + (c.x - p.x) * 0.5} ${c.y}, ${c.x} ${c.y}`;
  }
  const actIdx = etapas.findIndex((e) => e.status === "en_curso");
  const done = etapas.filter((e) => e.status === "completado").length;
  const prog = actIdx >= 0 ? (actIdx + 0.5) / etapas.length : done / etapas.length;

  return (
    <svg viewBox="0 0 100 100" preserveAspectRatio="none" className="absolute inset-0 w-full h-full pointer-events-none z-[2]">
      <defs>
        <linearGradient id="trailGlow" x1="0" y1="1" x2="0" y2="0">
          <stop offset="0%" stopColor="#3AE880" stopOpacity="0.7" />
          <stop offset={`${prog * 100}%`} stopColor="#66DDFF" stopOpacity="0.5" />
          <stop offset={`${Math.min(100, prog * 100 + 2)}%`} stopColor="#ffffff" stopOpacity="0.05" />
          <stop offset="100%" stopColor="#ffffff" stopOpacity="0.02" />
        </linearGradient>
        <filter id="trailBlur"><feGaussianBlur stdDeviation="0.3" /></filter>
      </defs>
      <path d={d} fill="none" stroke="#ffffff08" strokeWidth="0.3" strokeDasharray="0.8,1.5" />
      <path d={d} fill="none" stroke="url(#trailGlow)" strokeWidth="0.5" filter="url(#trailBlur)" strokeLinecap="round" />
      {/* Traveling particles */}
      <circle r="0.6" fill="#66EEFF" opacity="0.9" filter="url(#trailBlur)">
        <animateMotion dur="7s" repeatCount="indefinite" path={d} />
      </circle>
      <circle r="0.4" fill="#D488FF" opacity="0.7">
        <animateMotion dur="9s" begin="3s" repeatCount="indefinite" path={d} />
      </circle>
      <circle r="0.5" fill="#3AE880" opacity="0.8">
        <animateMotion dur="8s" begin="1.5s" repeatCount="indefinite" path={d} />
      </circle>
    </svg>
  );
}

// ── 3D Planet ────────────────────────────────────────────────────────────
function Planet3D({ etapa, index, status, onClick }) {
  const p = PLANETS[index % PLANETS.length];
  const pos = PLANET_POS[index % PLANET_POS.length];
  const isLocked = status === "siguiente";
  const isCurrent = status === "en_curso";
  const isDone = status === "completado";

  return (
    <motion.button
      data-testid={`planeta-${etapa.n}`}
      onClick={isLocked ? undefined : onClick}
      disabled={isLocked}
      className="absolute z-[5] group"
      style={{ left: `${pos.x}%`, top: `${pos.y}%`, transform: "translate(-50%, -50%)" }}
      animate={{ y: [0, -8, 0] }}
      transition={{ y: { duration: 4 + index * 0.6, repeat: Infinity, ease: "easeInOut" } }}
      whileHover={!isLocked ? { scale: 1.08, transition: { duration: 0.25 } } : {}}
    >
      {/* Outer pulse for current */}
      {isCurrent && (
        <motion.div
          className="absolute inset-[-20px] rounded-full"
          style={{ background: `radial-gradient(circle, ${p.glow}33, transparent 70%)` }}
          animate={{ scale: [1, 1.2, 1], opacity: [0.6, 0.2, 0.6] }}
          transition={{ duration: 3, repeat: Infinity, ease: "easeInOut" }}
        />
      )}
      {/* Planet sphere with 3D art */}
      <div
        className="relative rounded-full overflow-hidden"
        style={{
          width: "clamp(80px, 12vw, 130px)",
          height: "clamp(80px, 12vw, 130px)",
          boxShadow: isLocked
            ? "0 4px 30px rgba(0,0,0,0.7), inset 0 -6px 20px rgba(0,0,0,0.5)"
            : `0 0 40px rgba(${p.glowRgb},0.4), 0 0 80px rgba(${p.glowRgb},0.15), inset 0 -6px 20px rgba(0,0,0,0.3)`,
          filter: isLocked ? "grayscale(0.8) brightness(0.35)" : "none",
        }}
      >
        <img
          src={p.img}
          alt={p.name}
          className="w-full h-full object-cover"
          loading="eager"
          draggable={false}
        />
        {/* 3D light highlight */}
        <div
          className="absolute inset-0 rounded-full"
          style={{
            background: "radial-gradient(circle at 30% 25%, rgba(255,255,255,0.25) 0%, transparent 50%)",
          }}
        />
        {/* Lock overlay */}
        {isLocked && (
          <div className="absolute inset-0 bg-black/50 flex items-center justify-center">
            <Lock size={28} className="text-white/50 drop-shadow-lg" />
          </div>
        )}
        {/* Completed check */}
        {isDone && (
          <div className="absolute inset-0 flex items-center justify-center">
            <div className="w-10 h-10 rounded-full bg-[#1FA971]/90 flex items-center justify-center backdrop-blur-sm shadow-lg">
              <Star size={20} className="text-[#FFD700] fill-[#FFD700]" />
            </div>
          </div>
        )}
      </div>
      {/* Orbit ring */}
      {!isLocked && (
        <div
          className="absolute rounded-full border border-dashed pointer-events-none"
          style={{
            inset: "-14px",
            borderColor: `rgba(${p.glowRgb},0.2)`,
            animation: "spin 25s linear infinite",
          }}
        />
      )}
      {/* Name label */}
      <div className="absolute -bottom-9 left-1/2 -translate-x-1/2 w-[150px] sm:w-[190px] text-center whitespace-normal break-words leading-tight">
        <p className={`text-[11px] font-extrabold drop-shadow-lg ${isLocked ? "text-white/20" : "text-white/90"}`}>
          {etapa.titulo || p.name}
        </p>
        {etapa.enfoque && !isLocked && <p className="text-[9px] font-bold text-white/50">{etapa.enfoque}</p>}
        {isCurrent && (
          <motion.span
            className="text-[9px] font-black uppercase tracking-[0.15em] text-[#FFD700]"
            animate={{ opacity: [0.6, 1, 0.6] }}
            transition={{ duration: 2, repeat: Infinity }}
          >
            Explorando
          </motion.span>
        )}
        {isDone && (
          <span className="text-[9px] font-black uppercase tracking-[0.15em] text-[#3AE880] flex items-center justify-center gap-0.5">
            <Sparkles size={8} /> Dominado
          </span>
        )}
      </div>
    </motion.button>
  );
}

// ── Zarek Floating ───────────────────────────────────────────────────────
function ZarekFloat({ currentIndex, isComplete }) {
  const pos = PLANET_POS[currentIndex % PLANET_POS.length];
  const img = isComplete ? OWL_CELEBRATION : OWL_CLASSIC;
  return (
    <motion.div
      className="absolute z-[15] pointer-events-none"
      animate={{
        left: `${pos.x + 9}%`,
        top: `${pos.y - 8}%`,
        y: [0, -6, 0],
        rotate: [0, 3, -3, 0],
      }}
      transition={{
        left: { duration: 1.5, ease: "easeInOut" },
        top: { duration: 1.5, ease: "easeInOut" },
        y: { duration: 3, repeat: Infinity, ease: "easeInOut" },
        rotate: { duration: 6, repeat: Infinity, ease: "easeInOut" },
      }}
      data-testid="zarek-ship"
    >
      {/* Ship hull glow */}
      <div className="absolute inset-[-8px] rounded-full bg-[#FFD700]/10 blur-xl" />
      {/* Avatar */}
      <div className="relative">
        <img
          src={img}
          alt="Zarek"
          className="w-16 h-16 md:w-20 md:h-20 rounded-full object-cover border-2 border-[#FFD700]/40"
          style={{
            boxShadow: "0 0 20px rgba(255,215,0,0.3), 0 0 40px rgba(255,215,0,0.1)",
          }}
        />
        {/* Thruster flame */}
        <motion.div
          className="absolute -bottom-3 left-1/2 -translate-x-1/2 w-3 rounded-full"
          style={{ background: "linear-gradient(to bottom, #FFD700, #FF6B35, transparent)" }}
          animate={{ height: [12, 18, 12], opacity: [0.7, 1, 0.7] }}
          transition={{ duration: 0.8, repeat: Infinity }}
        />
        {/* Success animation */}
        {isComplete && (
          <motion.div
            className="absolute -top-2 -right-2"
            animate={{ rotate: [0, 20, -20, 0], scale: [1, 1.2, 1] }}
            transition={{ duration: 2, repeat: Infinity }}
          >
            <Trophy size={20} className="text-[#FFD700] fill-[#FFD700] drop-shadow-lg" />
          </motion.div>
        )}
      </div>
    </motion.div>
  );
}

// ── Landing Screen (Inside Planet) ───────────────────────────────────────
function LandingScreen({ etapa, index, onClose, onComplete, busy }) {
  const p = PLANETS[index % PLANETS.length];
  const isCurrent = etapa.status === "en_curso";
  const isDone = etapa.status === "completado";

  return (
    <motion.div
      className="fixed inset-0 z-50 overflow-hidden"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      data-testid={`planeta-detail-${etapa.n}`}
    >
      {/* Warp speed / landing effect */}
      <motion.div
        className="absolute inset-0"
        initial={{ scale: 5, filter: "blur(20px)", opacity: 0 }}
        animate={{ scale: 1, filter: "blur(0px)", opacity: 1 }}
        exit={{ scale: 3, filter: "blur(15px)", opacity: 0 }}
        transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
        style={{
          background: `radial-gradient(ellipse at center, rgba(${p.glowRgb},0.08) 0%, #080A1A 60%)`,
        }}
      >
        {/* Planet surface backdrop */}
        <div className="absolute inset-0 overflow-hidden">
          <img
            src={p.img}
            alt=""
            className="absolute top-0 left-1/2 -translate-x-1/2 w-[140%] max-w-none opacity-[0.07] blur-sm"
            style={{ maskImage: "radial-gradient(ellipse, black 20%, transparent 70%)" }}
          />
        </div>

        {/* Content container */}
        <div className="relative h-full flex flex-col max-w-2xl mx-auto px-4 pt-4 pb-6">
          {/* Header bar */}
          <motion.div
            initial={{ y: -30, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ delay: 0.3, duration: 0.4 }}
            className="flex items-center gap-3 mb-6 shrink-0"
          >
            <button
              onClick={onClose}
              data-testid="planeta-detail-close"
              className="w-10 h-10 rounded-xl bg-white/10 backdrop-blur-md flex items-center justify-center text-white/70 hover:text-white hover:bg-white/20 transition-all border border-white/10"
            >
              <ChevronLeft size={18} />
            </button>
            <div
              className="w-12 h-12 rounded-full overflow-hidden shrink-0 border-2"
              style={{ borderColor: `rgba(${p.glowRgb},0.5)`, boxShadow: `0 0 15px rgba(${p.glowRgb},0.3)` }}
            >
              <img src={p.img} alt="" className="w-full h-full object-cover" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-[10px] font-black uppercase tracking-[0.2em]" style={{ color: p.glow }}>
                Mundo {etapa.n}
              </p>
              <h2 className="text-lg font-black text-white leading-tight break-words">{etapa.titulo || p.name}</h2>
              {etapa.enfoque && <p className="text-[11px] font-bold text-white/60">{etapa.enfoque}</p>}
            </div>
            {isDone && (
              <div className="flex items-center gap-1 bg-[#1FA971]/20 px-3 py-1.5 rounded-full border border-[#1FA971]/30">
                <Star size={12} className="text-[#FFD700] fill-[#FFD700]" />
                <span className="text-[10px] font-black text-[#1FA971]">DOMINADO</span>
              </div>
            )}
          </motion.div>

          {/* Mission objective */}
          {etapa.objetivo && (
            <motion.div
              initial={{ y: 20, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ delay: 0.4 }}
              className="rounded-2xl p-4 mb-4 border border-white/10 backdrop-blur-md shrink-0"
              style={{ background: `linear-gradient(135deg, rgba(${p.glowRgb},0.08), rgba(${p.glowRgb},0.02))` }}
            >
              <p className="text-xs font-bold text-white/40 uppercase tracking-wider mb-1">Objetivo de la Misión</p>
              <p className="text-sm text-white/80 leading-relaxed">{etapa.objetivo}</p>
            </motion.div>
          )}

          {/* Missions as stars */}
          <div className="flex-1 overflow-y-auto space-y-3 min-h-0 pr-1 custom-scrollbar">
            <p className="text-[10px] font-black text-white/30 uppercase tracking-[0.2em] flex items-center gap-2">
              <Sparkles size={10} /> Misiones
            </p>
            {(etapa.lecciones || []).map((l, i) => (
              <motion.div
                key={i}
                initial={{ x: -30, opacity: 0 }}
                animate={{ x: 0, opacity: 1 }}
                transition={{ delay: 0.5 + i * 0.1 }}
                className="flex items-center gap-4 rounded-2xl px-5 py-4 border border-white/10 bg-white/[0.04] hover:bg-white/[0.08] hover:border-white/20 transition-all cursor-pointer group backdrop-blur-sm"
                data-testid={`planeta-${etapa.n}-mision-${i}`}
              >
                {/* Star icon */}
                <div
                  className="w-11 h-11 rounded-xl flex items-center justify-center shrink-0 transition-transform group-hover:scale-110"
                  style={{
                    background: `linear-gradient(135deg, rgba(${p.glowRgb},0.25), rgba(${p.glowRgb},0.1))`,
                    boxShadow: `0 0 15px rgba(${p.glowRgb},0.15)`,
                  }}
                >
                  <Play size={18} style={{ color: p.glow }} />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm text-white/90 font-bold leading-snug break-words">{l.titulo}</p>
                  <p className="text-[10px] text-white/40 mt-0.5">Misión {i + 1}</p>
                </div>
                <div className="w-8 h-8 rounded-full bg-white/5 flex items-center justify-center group-hover:bg-white/10 transition-colors">
                  <Star size={14} className="text-white/20 group-hover:text-[#FFD700] transition-colors" />
                </div>
              </motion.div>
            ))}
          </div>

          {/* Meta observable */}
          {etapa.meta && (
            <motion.div
              initial={{ y: 15, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ delay: 0.7 }}
              className="flex items-start gap-2.5 rounded-xl bg-[#FFD700]/10 border border-[#FFD700]/20 px-4 py-3 mt-4 shrink-0"
            >
              <Trophy size={14} className="text-[#FFD700] shrink-0 mt-0.5" />
              <p className="text-xs text-[#FFD700]/90 font-medium leading-relaxed">{etapa.meta}</p>
            </motion.div>
          )}

          {/* Complete button */}
          {isCurrent && (
            <motion.div
              initial={{ y: 20, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ delay: 0.8 }}
              className="mt-4 shrink-0"
            >
              <button
                onClick={() => onComplete(etapa.n)}
                disabled={busy}
                data-testid={`planeta-completar-${etapa.n}`}
                className="w-full py-3.5 rounded-2xl font-black text-sm text-white flex items-center justify-center gap-2 transition-all hover:scale-[1.02] active:scale-[0.98] disabled:opacity-50"
                style={{
                  background: `linear-gradient(135deg, ${p.glow}, ${PLANETS[(index + 1) % PLANETS.length]?.glow || p.glow})`,
                  boxShadow: `0 4px 25px rgba(${p.glowRgb},0.4)`,
                }}
              >
                {busy ? <Loader2 size={16} className="animate-spin" /> : <Rocket size={16} />}
                {busy ? "Despegando…" : "Misión Completada"}
              </button>
            </motion.div>
          )}
        </div>
      </motion.div>
    </motion.div>
  );
}

// ── HUD ──────────────────────────────────────────────────────────────────
function HUD({ progreso, etapas }) {
  return (
    <div className="absolute top-4 left-4 right-4 z-20 flex items-center gap-3" data-testid="mundos-hud">
      <div className="flex items-center gap-2.5 shrink-0">
        <img src={OWL_CLASSIC} alt="" className="w-9 h-9 rounded-full border-2 border-[#FFD700]/40 shadow-lg" />
        <div>
          <p className="text-xs font-black text-white/95 leading-tight">Los Mundos de Zarek</p>
          <p className="text-[9px] text-white/35 font-bold">Galaxia del Conocimiento</p>
        </div>
      </div>
      <div className="flex-1 max-w-xs">
        <div className="flex items-center justify-between mb-1">
          <span className="text-[9px] font-black text-white/30 uppercase tracking-wider">Progreso</span>
          <span className="text-[10px] font-black text-[#FFD700]">{progreso.completadas}/{progreso.total}</span>
        </div>
        <div className="h-2.5 rounded-full bg-white/10 overflow-hidden border border-white/5">
          <motion.div
            className="h-full rounded-full"
            style={{ background: "linear-gradient(90deg, #3AE880, #44BBFF, #C466FF, #FFD700)" }}
            initial={{ width: 0 }}
            animate={{ width: `${progreso.porcentaje}%` }}
            transition={{ duration: 1.5, ease: "easeOut" }}
          />
        </div>
      </div>
      {/* Mini planet indicators */}
      <div className="hidden md:flex gap-2">
        {etapas.map((e, i) => {
          const pl = PLANETS[i % PLANETS.length];
          const done = e.status === "completado";
          const active = e.status === "en_curso";
          return (
            <div
              key={i}
              className="w-5 h-5 rounded-full overflow-hidden border-2 transition-all"
              style={{
                borderColor: done ? "#3AE880" : active ? "#FFD700" : "#ffffff15",
                boxShadow: active ? "0 0 10px rgba(255,215,0,0.5)" : "none",
                filter: done || active ? "none" : "grayscale(1) brightness(0.3)",
              }}
            >
              <img src={pl.img} alt="" className="w-full h-full object-cover" />
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ── Avance de diagnósticos (v5.5.0) — sólo estados, nunca puntajes ───────
function DiagnosticosAvance({ cuestionarios, floating = false }) {
  const items = cuestionarios?.items || [];
  if (!items.length) return null;
  const pct = cuestionarios.total ? Math.round((cuestionarios.completados / cuestionarios.total) * 100) : 0;
  return (
    <div
      className={`${floating ? "absolute top-16 left-4 z-20 max-w-[320px]" : "relative mt-6 mx-auto max-w-sm"} rounded-2xl border border-white/10 bg-white/5 backdrop-blur px-3 py-2.5 text-left`}
      data-testid="ruta-diagnosticos"
    >
      <div className="flex items-center justify-between mb-1">
        <span className="text-[9px] font-black text-white/50 uppercase tracking-wider">Mis diagnósticos</span>
        <span className="text-[10px] font-black text-[#FFD700]" data-testid="ruta-diag-count">{cuestionarios.completados}/{cuestionarios.total}</span>
      </div>
      <div className="h-1.5 rounded-full bg-white/10 overflow-hidden mb-2">
        <div className="h-full rounded-full bg-gradient-to-r from-[#3AE880] to-[#44BBFF]" style={{ width: `${pct}%` }} />
      </div>
      <div className="flex flex-wrap gap-1.5">
        {items.slice(0, 8).map((it) => {
          const done = it.status === "completed";
          return (
            <span
              key={it.id || it.code}
              title={it.title || it.code}
              className={`text-[10px] font-extrabold rounded-full px-2 py-0.5 border ${done ? "text-[#3AE880] border-[#3AE880]/40 bg-[#3AE880]/10" : "text-white/50 border-white/15"}`}
            >
              {done ? "✓ " : "○ "}{it.code}
            </span>
          );
        })}
      </div>
    </div>
  );
}

// ── Speech Bubble ────────────────────────────────────────────────────────
function SpeechBubble({ message }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: 0.5 }}
      className="absolute bottom-4 left-4 right-4 z-20 flex justify-center"
      data-testid="zarek-speech"
    >
      <div className="bg-[#0d1025]/80 backdrop-blur-xl border border-white/10 rounded-2xl px-5 py-3 max-w-md flex items-center gap-3 shadow-[0_4px_30px_rgba(0,0,0,0.5)]">
        <img src={OWL_CLASSIC} alt="" className="w-9 h-9 rounded-full shrink-0 border border-[#FFD700]/30" />
        <p className="text-xs text-white/75 font-medium leading-relaxed">{message}</p>
      </div>
    </motion.div>
  );
}

// ── Main Component ───────────────────────────────────────────────────────
export default function EstudianteRuta() {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const [selectedIdx, setSelectedIdx] = useState(null);

  const load = useCallback(() =>
    api.get("/diagnostics/mi-ruta").then(({ data }) => setData(data)).catch(() => setData({ has_ruta: false })),
  []);
  useEffect(() => { load(); }, [load]);

  const marcarCompleta = async (n) => {
    setBusy(true);
    try {
      const { data: d } = await api.post("/diagnostics/mi-ruta/etapas/complete", { etapa: n });
      setData(d);
      setSelectedIdx(null);
    } finally {
      setBusy(false);
    }
  };

  if (!data) return (
    <div className="flex items-center justify-center h-[80vh]" style={{ background: "#080A1A" }}>
      <Loader2 className="animate-spin text-[#44BBFF]" size={32} />
    </div>
  );

  if (!data.has_ruta) return (
    <div className="relative flex flex-col items-center justify-center h-[80vh] text-center px-4 overflow-hidden" style={{ background: "#080A1A" }} data-testid="mi-ruta-empty">
      <StarField /><Nebulae /><ShootingStars />
      <motion.div initial={{ scale: 0.8, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} className="relative z-10">
        <img src={OWL_CLASSIC} alt="Zarek" className="w-28 h-28 mx-auto mb-5 drop-shadow-[0_0_20px_rgba(255,215,0,0.3)]" />
        <h1 className="text-2xl font-black text-white">Los Mundos de Zarek</h1>
        <p className="text-white/40 mt-3 max-w-xs mx-auto text-sm">
          Zarek está cartografiando tu galaxia personal. Cuando completes tus evaluaciones (CEP y CAD), tus mundos aparecerán aquí.
        </p>
        <DiagnosticosAvance cuestionarios={data.cuestionarios} />
        <div className="flex justify-center gap-4 mt-6">
          {PLANETS.map((p, i) => (
            <div key={i} className="w-10 h-10 rounded-full overflow-hidden opacity-25 blur-[2px] border border-white/10">
              <img src={p.img} alt="" className="w-full h-full object-cover" />
            </div>
          ))}
        </div>
      </motion.div>
    </div>
  );

  const { etapas, progreso } = data;
  const currentIdx = etapas.findIndex((e) => e.status === "en_curso");
  const zarekIdx = currentIdx >= 0 ? currentIdx : etapas.filter((e) => e.status === "completado").length;
  const isComplete = progreso.porcentaje === 100;

  const msg = isComplete
    ? "Has conquistado toda la galaxia. Zarek celebra tu victoria, explorador."
    : currentIdx >= 0
    ? `Estás orbitando ${etapas[currentIdx]?.titulo || PLANETS[currentIdx % PLANETS.length]?.name}. Aterriza y completa las misiones.`
    : "Tu aventura comienza ahora. Haz clic en el primer planeta para explorar.";

  return (
    <div
      className="relative w-full overflow-hidden"
      style={{ height: "calc(100vh - 64px)", background: "radial-gradient(ellipse at 50% 100%, #0d1025, #080A1A 55%, #030412)" }}
      data-testid="mundos-zarek"
    >
      <StarField />
      <Nebulae />
      <ShootingStars />
      <EnergyPath etapas={etapas} />
      <HUD progreso={progreso} etapas={etapas} />
      <DiagnosticosAvance cuestionarios={data.cuestionarios} floating />

      {etapas.map((e, i) => (
        <Planet3D
          key={e.n}
          etapa={e}
          index={i}
          status={e.status}
          onClick={() => setSelectedIdx(i)}
        />
      ))}

      <ZarekFloat currentIndex={zarekIdx} isComplete={isComplete} />
      <SpeechBubble message={msg} />

      <AnimatePresence>
        {selectedIdx !== null && etapas[selectedIdx] && (
          <LandingScreen
            etapa={etapas[selectedIdx]}
            index={selectedIdx}
            onClose={() => setSelectedIdx(null)}
            onComplete={marcarCompleta}
            busy={busy}
          />
        )}
      </AnimatePresence>

      <style>{`
        @keyframes twinkle {
          0% { opacity: 0.15; transform: scale(1); }
          100% { opacity: 1; transform: scale(1.4); }
        }
        @keyframes spin {
          from { transform: rotate(0deg); } to { transform: rotate(360deg); }
        }
        @keyframes shootingStar {
          0% { transform: rotate(-35deg) translateY(-50px); opacity: 0; }
          5% { opacity: 1; }
          30% { opacity: 0.8; }
          100% { transform: rotate(-35deg) translateY(calc(100vh + 100px)); opacity: 0; }
        }
        .custom-scrollbar::-webkit-scrollbar { width: 3px; }
        .custom-scrollbar::-webkit-scrollbar-track { background: transparent; }
        .custom-scrollbar::-webkit-scrollbar-thumb { background: rgba(255,255,255,0.1); border-radius: 3px; }
      `}</style>
    </div>
  );
}
